import { FlutterChannel } from "./enums/channel";

/**
 * Checks if a specific Flutter JavaScript channel is available on window
 */
function hasChannel(channelName: FlutterChannel): boolean {
  if (typeof window === "undefined") return false;
  const channel = (window as any)[channelName];
  return (
    typeof channel !== "undefined" && typeof channel.postMessage === "function"
  );
}

// Bootstrap window.flutter if any known Flutter channel is pre-injected
if (
  typeof window !== "undefined" &&
  (hasChannel(FlutterChannel.CLIPBOARD) ||
    hasChannel(FlutterChannel.REQUEST_NATIVE_APP_CHECK_TOKEN) ||
    hasChannel(FlutterChannel.SHARE) ||
    hasChannel(FlutterChannel.FCM) ||
    hasChannel(FlutterChannel.SET_LANGUAGE))
) {
  (window as any).flutter = true;
}

/**
 * Checks if the app is running inside Flutter webview
 */
export function isRunningInFlutter(): boolean {
  return (
    !!(window as any).flutter ||
    hasChannel(FlutterChannel.CLIPBOARD) ||
    hasChannel(FlutterChannel.REQUEST_NATIVE_APP_CHECK_TOKEN) ||
    hasChannel(FlutterChannel.SHARE) ||
    hasChannel(FlutterChannel.FCM) ||
    hasChannel(FlutterChannel.SET_LANGUAGE)
  );
}

/**
 * Checks if Flutter ad banner support is available
 */
export function hasSupportForAdBanners(): boolean {
  return (
    (!!window.flutter && !!window.bannerAdSupported) ||
    hasChannel(FlutterChannel.SHOW_BANNER_AD)
  );
}

/**
 * Checks if native Apple authentication is supported
 */
export function hasSupportForNativeAppleAuth(): boolean {
  return (
    (!!window.flutter && !!window.nativeAppleAuthSupported) ||
    hasChannel(FlutterChannel.APPLE_ID)
  );
}

/**
 * Checks if native Google authentication is supported
 */
export function hasSupportForNativeGoogleAuth(): boolean {
  return (
    (!!window.flutter && !!window.nativeGoogleAuthSupported) ||
    hasChannel(FlutterChannel.GOOGLE_ID)
  );
}

/**
 * Checks if Flutter share functionality is supported
 */
export function hasSupportForFlutterShare(): boolean {
  return (
    (!!window.flutter && !!window.nativeShareSupported) ||
    hasChannel(FlutterChannel.SHARE)
  );
}

/**
 * Checks if the Flutter wrapper supports app review features (store listing, review dialog)
 */
export function hasSupportForAppReviewFeature(): boolean {
  return (
    (!!window.flutter && !!window.appReviewSupported) ||
    (hasChannel(FlutterChannel.REQUEST_APP_REVIEW) &&
      hasChannel(FlutterChannel.OPEN_STORE_LISTING))
  );
}

/**
 * Checks if the native in-app review dialog is available on this device
 */
export function isAppReviewDialogAvailable(): boolean {
  return hasSupportForAppReviewFeature() && !!window.appReviewAvailable;
}

/**
 * Checks if the flutter wrapper supports native app check tokens
 */
export function hasSupportForNativeAppCheck(): boolean {
  return (
    (!!window.flutter && !!window.nativeAppCheckSupported) ||
    hasChannel(FlutterChannel.REQUEST_NATIVE_APP_CHECK_TOKEN)
  );
}

/**
 * Checks if the flutter wrapper supports native camera shorts recording
 */
export function hasSupportForNativeShorts(): boolean {
  return (
    (!!window.flutter && !!(window as any).nativeShortsSupported) ||
    hasChannel(FlutterChannel.CAMERA)
  );
}

/**
 * Checks if the flutter wrapper supports syncing user language
 */
export function hasSupportForSetLanguage(): boolean {
  return (
    (!!window.flutter && !!(window as any).languageSyncSupported) ||
    hasChannel(FlutterChannel.SET_LANGUAGE)
  );
}
